/** A quick live's page: the room's read, who came (every page of them), and the replay email's own time, kept fresh while it changes. */
import { useCallback, useEffect, useState } from "react";
import type { RoomAudienceSession, RoomPerson } from "../../../shared/types";
import { api } from "../../lib/api";
import { pageReplay } from "./quickWords";
import { POLL_MS } from "./useRoomPage";
import { useRoom } from "./useRoom";

/** Every person who came, page after page: the follow-up reaches all of them, not just the first page. */
async function allCame(id: string, sessionId: string): Promise<RoomPerson[]> {
  const people: RoomPerson[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.webinars.audienceGroup(id, "came", sessionId, cursor);
    people.push(...page.people);
    cursor = page.next ?? undefined;
  } while (cursor);
  return people;
}

export function useQuickLive(id: string) {
  const { detail, phase, refresh } = useRoom(id);
  const live = detail?.live;
  const sessionId = live?.sessionId ?? "";
  const state = live?.state;
  const replay = live ? pageReplay(live) : "none";
  const [people, setPeople] = useState<RoomPerson[] | null>(null);
  const [mail, setMail] = useState<RoomAudienceSession["replayMail"]>(null);
  const [again, setAgain] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    allCame(id, sessionId)
      .then((p) => alive && setPeople(p))
      .catch(() => alive && setPeople((p) => p ?? []));
    if (state === "ended") {
      api.webinars
        .audience(id, sessionId)
        .then((a) => alive && setMail(a.session?.replayMail ?? null))
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [id, sessionId, state, again]);

  // While it is on air, or its replay is being made or its email is still to go, the page asks again.
  const moving = state === "live" || (state === "ended" && replay !== "none" && mail === null);
  useEffect(() => {
    if (!moving) return;
    const iv = window.setInterval(() => {
      void refresh().catch(() => undefined);
      setAgain((n) => n + 1);
    }, POLL_MS);
    return () => window.clearInterval(iv);
  }, [moving, refresh]);

  const reload = useCallback(() => {
    void refresh().catch(() => undefined);
    setAgain((n) => n + 1);
  }, [refresh]);

  return { detail, phase, people, mail, reload };
}
